/**
 * Shared display components for the Candidate Excitement Score (PR2 — read only).
 *
 * Surfaces wired against these in the v2 diagrammation:
 *   - <CandidateExcitementHeaderBlock />: fixed block at the top of the
 *     candidate detail modal, between the candidate header and the tabs row.
 *     Visible regardless of which tab is selected. Shows latest score +
 *     trajectory + rationale, with an alert tone when the score is low or
 *     the trajectory is descending.
 *   - <CandidateExcitementInterviewBlock />: per-interview block rendered
 *     alongside the GCA/Role/COMP verdicts in the candidate detail modal's
 *     Entrevistas tab. Shows the score that the IA read from THAT specific
 *     interview's transcript, with a delta vs the prior stage.
 *   - <OfferExcitementBlock />: rendered next to the manager excitement_score
 *     in the offer creation dialog. Shows both sides + the closing gap.
 *
 * All displays honor the HM override: when `overriddenScore != null` the pill
 * uses the override and an italic hint reveals the original IA score (override
 * flow itself lands in PR3, but readers must see the effective number now).
 *
 * No icons (per repo convention) — only colored dots/pills and text labels.
 */
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";
import { cn } from "@/lib/utils";

type OverrideCategory =
  | "ai_missed_signal"
  | "ai_misread_tone"
  | "context_outside_transcript"
  | "ai_correct_but_stale"
  | "other";

const OVERRIDE_CATEGORY_OPTIONS: { value: OverrideCategory; label: string }[] = [
  { value: "ai_missed_signal", label: "Não capturou sinal importante" },
  { value: "ai_misread_tone", label: "Leu o tom errado" },
  { value: "context_outside_transcript", label: "Tenho info de fora do transcript" },
  { value: "ai_correct_but_stale", label: "IA estava certa, mas mudou desde a entrevista" },
  { value: "other", label: "Outro" },
];

// ────────────────────────────────────────────────────────────
// Types — kept narrow so client doesn't depend on server types.
// ────────────────────────────────────────────────────────────

export interface CandidateExcitementScoreRow {
  /** Stable row id — AI row id when present, falls back to "interview:<id>". */
  id: string;
  interviewId: string;
  stageSequence: number;
  /** Interviewer's own reading captured in the feedback dialog. Takes
   * precedence over the AI value when present. */
  interviewerScore: number | null;
  interviewerNote: string | null;
  /** AI row id — null when the AI compute hasn't produced a row yet. The
   * override flow requires this id, so its absence disables override. */
  aiScoreRowId: string | null;
  /** AI-inferred score from transcript. Null when AI compute hasn't run. */
  excitementScore: number | null;
  rationale: string | null;
  confidence: string | null;
  signals: Array<{
    signal: string;
    source: string[];
    convergent: boolean;
    points: number;
  }> | null;
  /** HM override on the AI row (post-hoc correction). */
  overriddenScore: number | null;
  overrideReason: string | null;
  overrideCategory: string | null;
  overriddenAt: string | Date | null;
}

// ────────────────────────────────────────────────────────────
// Color buckets — semaphore mapped to the warm-palette tokens.
// ────────────────────────────────────────────────────────────

type Band = "cold" | "warm" | "hot";

function bandFor(score: number | null | undefined): Band {
  if (score == null) return "warm";
  if (score <= 3) return "cold";
  if (score <= 6) return "warm";
  return "hot";
}

function bandLabel(band: Band): string {
  // Intensity labels — agree with the implicit "animação" (feminine), so they
  // never gender-clash with the candidate ("Maria está animado" → wrong).
  switch (band) {
    case "cold":
      return "baixa";
    case "warm":
      return "média";
    case "hot":
      return "alta";
  }
}

const dotClasses: Record<Band, string> = {
  cold: "bg-orange-700",
  warm: "bg-amber-600",
  hot: "bg-emerald-700",
};

const pillClasses: Record<Band, string> = {
  cold: "bg-orange-50 text-orange-700",
  warm: "bg-amber-50 text-amber-800",
  hot: "bg-emerald-50 text-emerald-800",
};

const stepClasses: Record<Band, string> = {
  cold: "bg-orange-50 text-orange-700",
  warm: "bg-amber-50 text-amber-800",
  hot: "bg-emerald-50 text-emerald-800",
};

// ────────────────────────────────────────────────────────────
// Score helpers.
// ────────────────────────────────────────────────────────────

/**
 * Effective score precedence:
 *   1. overriddenScore  — HM post-hoc correction (off-call context)
 *   2. interviewerScore — human in the room, captured at feedback time
 *   3. excitementScore  — IA from transcript (calibration baseline)
 * Returns null when none of the three is set.
 */
export function effectiveScore(row: CandidateExcitementScoreRow): number | null {
  if (row.overriddenScore != null) return row.overriddenScore;
  if (row.interviewerScore != null) return row.interviewerScore;
  return row.excitementScore;
}

function wasOverridden(row: CandidateExcitementScoreRow): boolean {
  return row.overriddenScore != null;
}

/** True when the IA inference disagrees with the interviewer's reading.
 * Used to surface the IA value as a calibration hint when interviewer is
 * the canonical signal. */
function interviewerDisagreesWithAI(row: CandidateExcitementScoreRow): boolean {
  return (
    row.interviewerScore != null &&
    row.excitementScore != null &&
    row.interviewerScore !== row.excitementScore
  );
}

/** A trajectory is "descending" when the latest score is at least 2 below
 * the peak across the series, and the series has more than one entry. */
function isDescending(scores: CandidateExcitementScoreRow[]): boolean {
  const values = scores.map(effectiveScore).filter((v): v is number => v != null);
  if (values.length < 2) return false;
  const peak = Math.max(...values);
  const last = values[values.length - 1];
  return peak - last >= 2;
}

// ────────────────────────────────────────────────────────────
// Primitives.
// ────────────────────────────────────────────────────────────

interface ScorePillProps {
  score: number;
  size?: "sm" | "md";
  showLabel?: boolean;
}

export function ScorePill({ score, size = "sm", showLabel = false }: ScorePillProps) {
  const band = bandFor(score);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full font-semibold",
        size === "sm" ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-sm",
        pillClasses[band],
      )}
    >
      <span className={cn("inline-block h-1.5 w-1.5 rounded-full", dotClasses[band])} />
      {score}
      {showLabel ? <span className="font-normal opacity-80">— {bandLabel(band)}</span> : null}
    </span>
  );
}

interface OverrideHintProps {
  row: CandidateExcitementScoreRow;
}

/** Reveals where the displayed value came from when there's disagreement
 * between signals. Three states:
 *   1. HM override applied  → "ajustado pelo HM · IA havia inferido X"
 *   2. Interviewer disagrees with AI → "IA havia inferido X"
 *   3. None of the above → nothing renders. */
export function OverrideHint({ row }: OverrideHintProps) {
  if (wasOverridden(row)) {
    return (
      <span className="text-[11px] italic text-muted-foreground">
        ajustado pelo HM
        {row.excitementScore != null ? ` · IA havia inferido ${row.excitementScore}` : ""}
      </span>
    );
  }
  if (interviewerDisagreesWithAI(row)) {
    return (
      <span className="text-[11px] italic text-muted-foreground">
        IA havia inferido {row.excitementScore}
      </span>
    );
  }
  return null;
}

interface TrajectoryProps {
  scores: CandidateExcitementScoreRow[];
}

export function Trajectory({ scores }: TrajectoryProps) {
  const renderable = scores.filter((s) => effectiveScore(s) != null);
  if (renderable.length === 0) return null;
  return (
    <div className="flex items-center gap-1">
      {renderable.map((row, idx) => {
        const value = effectiveScore(row) as number;
        const band = bandFor(value);
        const titleParts: string[] = [`Etapa ${row.stageSequence}: ${value}`];
        if (wasOverridden(row)) titleParts.push(`ajustado pelo HM (IA: ${row.excitementScore})`);
        else if (
          row.interviewerScore != null &&
          row.excitementScore != null &&
          row.interviewerScore !== row.excitementScore
        )
          titleParts.push(`leitura do entrevistador (IA: ${row.excitementScore})`);
        return (
          <span key={row.id} className="flex items-center gap-1">
            <span
              className={cn(
                "inline-flex h-6 w-6 items-center justify-center rounded-md text-xs font-bold",
                stepClasses[band],
              )}
              title={titleParts.join(" · ")}
            >
              {value}
            </span>
            {idx < renderable.length - 1 ? (
              <span className="text-xs text-muted-foreground">→</span>
            ) : null}
          </span>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Surface 1: Candidate detail modal — header pill.
// Compact pill rendered next to the candidate name. The whole panel
// (trajectory + last rationale + link to history) is hidden behind
// a popover, opened on click. Default state is just the score so we
// don't crowd the header with information that's only situationally
// useful.
// ────────────────────────────────────────────────────────────

interface HeaderBlockProps {
  scores: CandidateExcitementScoreRow[];
  /** Application id — needed so the override mutation can invalidate the
   * right query cache after a successful save. */
  applicationId: string;
  /** Called when the user clicks "Ver leituras por etapa →" in the popover.
   * Host is expected to switch to the Entrevistas section and (optionally)
   * scroll/highlight the interview blocks that have an excitement score. */
  onShowHistory?: () => void;
}

interface OverrideFormProps {
  row: CandidateExcitementScoreRow;
  applicationId: string;
  onClose: () => void;
}

/**
 * Edit-in-place form rendered inside the header popover. Posts to the
 * override RPC and invalidates the application's series so the parent
 * surfaces update immediately. Designed as a quick HM action — no
 * inline confirmation, single Save / Cancel.
 */
function OverrideForm({ row, applicationId, onClose }: OverrideFormProps) {
  const { toast } = useToast();
  const initial = effectiveScore(row) ?? 5;
  const [score, setScore] = useState<number>(initial);
  const [category, setCategory] = useState<OverrideCategory>("ai_misread_tone");
  const [reason, setReason] = useState<string>(row.overrideReason ?? "");

  const aiRowId = row.aiScoreRowId;

  const mutation = useMutation({
    mutationFn: async () => {
      if (!aiRowId) {
        throw new Error(
          "Não dá pra ajustar antes da IA terminar de processar — aguarde alguns segundos.",
        );
      }
      return rpcClient.candidateExcitement.override({
        scoreId: aiRowId,
        overriddenScore: score,
        overrideReason: reason.trim(),
        overrideCategory: category,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.candidateExcitement.listByApplication.queryOptions({
          input: { applicationId },
        }).queryKey,
      });
      toast({ title: "Score ajustado", description: "A oferta vai considerar o novo valor." });
      onClose();
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Erro ao salvar override.";
      toast({ title: "Não foi possível ajustar", description: message, variant: "destructive" });
    },
  });

  const reasonOk = reason.trim().length > 0;
  const changedScore = row.excitementScore == null || score !== row.excitementScore;
  const canSubmit = !!aiRowId && reasonOk && changedScore && !mutation.isPending;

  return (
    <div className="px-3.5 py-3 space-y-2.5">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Ajustar score
        {row.excitementScore != null ? ` · IA inferiu ${row.excitementScore}` : ""}
      </div>
      <div className="flex items-center gap-2">
        <label htmlFor="excitement-override-score" className="text-xs text-muted-foreground w-12">
          Score
        </label>
        <Input
          id="excitement-override-score"
          type="number"
          min={0}
          max={10}
          value={score}
          onChange={(e) => {
            const next = Number(e.target.value);
            if (Number.isInteger(next) && next >= 0 && next <= 10) setScore(next);
          }}
          className="h-7 w-16 text-xs"
          data-testid="input-override-score"
        />
        <span className="text-[11px] text-muted-foreground">de 0 a 10</span>
      </div>
      <div className="space-y-1">
        <label htmlFor="excitement-override-category" className="text-xs text-muted-foreground">
          Por que a IA errou?
        </label>
        <Select value={category} onValueChange={(v) => setCategory(v as OverrideCategory)}>
          <SelectTrigger
            id="excitement-override-category"
            className="h-7 text-xs"
            data-testid="select-override-category"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OVERRIDE_CATEGORY_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-xs">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <label htmlFor="excitement-override-reason" className="text-xs text-muted-foreground">
          Comentário (obrigatório)
        </label>
        <Textarea
          id="excitement-override-reason"
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="O que mudou ou foi mal lido pela IA?"
          className="text-xs resize-none"
          data-testid="textarea-override-reason"
        />
      </div>
      <div className="flex items-center justify-end gap-2 pt-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={onClose}
          disabled={mutation.isPending}
        >
          Cancelar
        </Button>
        <Button
          size="sm"
          className="h-7 text-xs"
          onClick={() => mutation.mutate()}
          disabled={!canSubmit}
          data-testid="button-override-save"
        >
          {mutation.isPending ? "Salvando…" : "Salvar"}
        </Button>
      </div>
    </div>
  );
}

/**
 * Pulls the "Resumo: …" line from the rationale (the first sentence the
 * skill writes before evidences and closing risk). Falls back to the first
 * non-empty line if the prompt format ever changes. Bounded to ~140 chars
 * so it fits in the popover without scrolling.
 */
function extractSummary(rationale: string): string {
  const trimmed = rationale.trim();
  if (!trimmed) return "";
  const match = trimmed.match(/^Resumo:\s*([^\n]+)/i);
  const summary = match ? match[1].trim() : (trimmed.split(/\n+/)[0] ?? "");
  if (summary.length <= 140) return summary;
  return `${summary.slice(0, 137).trimEnd()}…`;
}

function TrendCue({ scores }: { scores: CandidateExcitementScoreRow[] }) {
  const values = scores.map(effectiveScore).filter((v): v is number => v != null);
  if (values.length < 2) return null;
  const descending = isDescending(scores);
  const last = values[values.length - 1];
  const peak = Math.max(...values);
  // Only annotate the cue when the trajectory has something to say.
  if (descending) {
    return <span className="text-[11px] font-medium text-orange-700">esfriou ↓</span>;
  }
  if (last > peak - 1 && last >= 6) {
    return <span className="text-[11px] font-medium text-emerald-700">crescente ↑</span>;
  }
  return null;
}

export function CandidateExcitementHeaderBlock({
  scores,
  applicationId,
  onShowHistory,
}: HeaderBlockProps) {
  const [editing, setEditing] = useState(false);
  if (scores.length === 0) return null;
  const latest = scores[scores.length - 1];
  const latestValue = effectiveScore(latest);
  if (latestValue == null) return null;
  const descending = isDescending(scores);
  const alert = descending || latestValue <= 3;
  const band = bandFor(latestValue);

  return (
    <Popover onOpenChange={(open) => !open && setEditing(false)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold transition-shadow",
            "hover:shadow-[0_0_0_3px_rgba(28,40,64,0.06)]",
            pillClasses[band],
          )}
          aria-label="Ver detalhes da animação do candidato"
          data-testid="button-excitement-pill"
        >
          <span className={cn("inline-block h-1.5 w-1.5 rounded-full", dotClasses[band])} />
          <span>{latestValue}</span>
          <span className="font-normal opacity-75">·</span>
          <span className="font-normal opacity-90">
            {alert && descending ? "esfriou ↓" : bandLabel(band)}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-72 p-0">
        {editing ? (
          <OverrideForm
            row={latest}
            applicationId={applicationId}
            onClose={() => setEditing(false)}
          />
        ) : (
          <>
            <div className="px-3.5 pt-3 pb-2.5">
              <div className="mb-2 flex items-center gap-2">
                {scores.length > 1 ? (
                  <Trajectory scores={scores} />
                ) : (
                  <ScorePill score={latestValue} size="sm" />
                )}
                <span className="ml-auto">
                  <TrendCue scores={scores} />
                </span>
              </div>
              {latest.rationale ? (
                <p className="text-xs leading-snug text-foreground/80">
                  {extractSummary(latest.rationale)}
                </p>
              ) : null}
              <div className="mt-1.5">
                <OverrideHint row={latest} />
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-border px-3.5 py-2 gap-2">
              {onShowHistory ? (
                <button
                  type="button"
                  onClick={onShowHistory}
                  className="text-xs font-medium text-primary hover:underline"
                  data-testid="button-excitement-show-history"
                >
                  Ver leituras por etapa →
                </button>
              ) : (
                <span />
              )}
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-xs font-medium text-muted-foreground hover:text-foreground"
                data-testid="button-excitement-override"
              >
                Ajustar
              </button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ────────────────────────────────────────────────────────────
// Surface 2: Per-interview inline block.
// Rendered in the interviews tab of the candidate detail modal,
// alongside the GCA/Role/COMP verdict blocks.
// ────────────────────────────────────────────────────────────

interface InterviewBlockProps {
  /** All scores for the application — component picks the one matching interviewId. */
  scores: CandidateExcitementScoreRow[];
  interviewId: string;
  /** When true, briefly outlines the block — used when the user arrives here
   * from the header pill's "Ver leituras por etapa →" link to indicate the
   * landing point. The host is responsible for clearing the highlight on a
   * timer; this component just renders the ring. */
  highlighted?: boolean;
}

export function CandidateExcitementInterviewBlock({
  scores,
  interviewId,
  highlighted = false,
}: InterviewBlockProps) {
  const [expanded, setExpanded] = useState(false);
  const row = scores.find((s) => s.interviewId === interviewId);
  if (!row) return null;
  const value = effectiveScore(row);
  if (value == null) return null;
  const band = bandFor(value);

  // Compute delta vs prior stage (by stage_sequence).
  const sorted = [...scores].sort((a, b) => a.stageSequence - b.stageSequence);
  const idx = sorted.findIndex((s) => s.interviewId === interviewId);
  const prev = idx > 0 ? sorted[idx - 1] : null;
  const prevValue = prev ? effectiveScore(prev) : null;
  const delta = prevValue != null ? value - prevValue : null;

  const borderToneClass =
    band === "cold"
      ? "border-l-orange-700"
      : band === "warm"
        ? "border-l-amber-600"
        : "border-l-emerald-700";
  const bgToneClass =
    band === "cold" ? "bg-orange-50/40" : band === "warm" ? "bg-amber-50/40" : "bg-emerald-50/40";
  const highlightClass = highlighted
    ? "ring-2 ring-primary/40 ring-offset-1 transition-shadow duration-500"
    : "";

  const summary = row.rationale ? extractSummary(row.rationale) : "";
  const hasMore = row.rationale ? row.rationale.trim().length > summary.length + 5 : false;

  return (
    <div
      className={cn(
        "rounded-md border-l-2 px-3 py-2",
        borderToneClass,
        bgToneClass,
        highlightClass,
      )}
    >
      <div className="mb-1 flex items-center justify-between">
        <p className="text-xs font-medium text-muted-foreground">
          Animação do candidato (IA do transcript)
        </p>
        <div className="flex items-center gap-2">
          <ScorePill score={value} />
          {delta != null && delta !== 0 ? (
            <span
              className={cn(
                "text-[11px] font-medium",
                delta > 0 ? "text-emerald-700" : "text-orange-700",
              )}
            >
              {delta > 0 ? "↑" : "↓"} {delta > 0 ? `+${delta}` : delta}
            </span>
          ) : null}
        </div>
      </div>
      {row.rationale ? (
        expanded ? (
          <p className="whitespace-pre-line text-sm text-foreground/80">{row.rationale}</p>
        ) : (
          <p className="text-sm text-foreground/80">{summary}</p>
        )
      ) : null}
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <OverrideHint row={row} />
        {hasMore ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="ml-auto text-[11px] font-medium text-muted-foreground hover:text-foreground"
            data-testid={`button-excitement-toggle-${row.interviewId}`}
          >
            {expanded ? "Recolher ▴" : "Ver evidências ▾"}
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Surface 3: Offer dialog block — side-by-side with manager excitement.
// ────────────────────────────────────────────────────────────

interface OfferBlockProps {
  scores: CandidateExcitementScoreRow[];
  managerScore: number | null;
}

export function OfferExcitementBlock({ scores, managerScore }: OfferBlockProps) {
  if (scores.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border px-4 py-3 text-xs text-muted-foreground">
        Animação do candidato ainda não foi calculada — necessário ao menos uma entrevista completa
        com transcript disponível.
      </div>
    );
  }
  const latest = scores[scores.length - 1];
  const candidateValue = effectiveScore(latest);
  if (candidateValue == null) {
    return (
      <div className="rounded-lg border border-dashed border-border px-4 py-3 text-xs text-muted-foreground">
        Animação do candidato ainda não foi avaliada nesta etapa.
      </div>
    );
  }
  const gap = managerScore != null ? Math.max(0, managerScore - candidateValue) : null;

  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-4">
      <div className="text-sm font-semibold text-foreground">Calibração de excitement</div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-md bg-muted/40 p-3">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            HM → candidato
          </div>
          {managerScore != null ? (
            <ScorePill score={managerScore} size="md" showLabel />
          ) : (
            <span className="text-xs text-muted-foreground">Não calculado ainda</span>
          )}
        </div>

        <div className="rounded-md bg-muted/40 p-3">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Candidato → Comp
          </div>
          <ScorePill score={candidateValue} size="md" showLabel />
          <div className="mt-1">
            <OverrideHint row={latest} />
          </div>
        </div>
      </div>

      {gap != null && gap > 0 ? (
        <div className="rounded-md border-l-2 border-l-primary bg-primary/5 px-3 py-2">
          <div className="text-xs font-semibold text-primary">Gap de fechamento: {gap}</div>
          <p className="mt-0.5 text-xs leading-relaxed text-foreground">
            HM quer mais do que o candidato sinaliza querer. Considere oferta mais agressiva dentro
            da banda para cobrir risco de perder. (Modulação automática na fórmula chega no PR3.)
          </p>
        </div>
      ) : null}

      {scores.length > 1 ? (
        <div>
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Trajetória ao longo do funil
          </div>
          <Trajectory scores={scores} />
        </div>
      ) : null}

      {latest.rationale ? (
        <div>
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Última leitura
          </div>
          <p className="whitespace-pre-line text-xs leading-relaxed text-muted-foreground">
            {latest.rationale}
          </p>
        </div>
      ) : null}
    </div>
  );
}
